import { Link } from "react-router-dom";
import { useSiteConfig } from "@/lib/siteConfig";

// Udyam (MSME) registration, 2 October 2026.
const UDYAM_NO = "UDYAM-TN-02-0514748";

/**
 * The site footer — the same one, on every page.
 *
 * It used to exist only on the landing page, so the disclaimer link and the two ways to reach
 * support disappeared the moment anyone signed in. That is exactly backwards for a legal-services
 * platform: the customer reading their own stamped document is the one most likely to need the
 * disclaimer or a support address in front of them.
 *
 * The email and WhatsApp number come from `GET /site-config`, which admin edits in Settings — so
 * changing where support goes is a settings change, not a deploy. Cached across instances by
 * `useSiteConfig`.
 */
export default function SiteFooter({ className = "" }) {
  const cfg = useSiteConfig();
  // **Who this business is comes from the database, never from this file.** `company_name` and the
  // registered `address` are Company details in Admin → Settings (`config` table, published on the
  // public `GET /site-config`), and they are the same two values the invoice's FROM block renders
  // from — so the copyright line, the privacy policy's data-fiduciary clause and the bill cannot
  // disagree about the legal name or where the business is. This line used to read "DharmaTN Legal
  // Services · Chennai, Tamil Nadu" as literal text while the setting said "DharmaTN Legal LLP".
  //
  // The address is stored multi-line (a textarea) and flattened to one line here. Displaying it is
  // not decoration: the Consumer Protection (E-Commerce) Rules 2020 require the principal
  // geographic address to be shown, and the footer is on every page.
  const company = (cfg.company_name || "DharmaTN").trim();
  const address = (cfg.address || "").split("\n").map(l => l.trim()).filter(Boolean).join(", ");
  return (
    <footer className={`border-t border-white/5 mt-16 ${className}`} data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-xs text-[#6B7280]">
        <div className="space-y-1">
          <div data-testid="footer-company">
            © {new Date().getFullYear()} {company}{address && <> · {address}</>}
          </div>
          {/* Literal, unlike the name and address above: a Udyam number is issued once and never
              changes, so a setting would only be a way to mistype it. Worded as the registration it
              is — Udyam is a self-declaration, so nothing here may read as "Government approved". */}
          <div data-testid="footer-udyam">MSME (Udyam) Registration No. {UDYAM_NO}</div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {/* A new tab, deliberately. This footer is on every page including the wizard and the
              cart, where leaving costs the reader unsaved form state and a chosen delivery
              address. The page it opens knows it was opened this way and offers Close rather than
              Back (see components/LegalShell.jsx). */}
          <Link to="/disclaimer" target="_blank" rel="noopener noreferrer" data-testid="footer-disclaimer"
                className="hover:text-white transition-colors">Disclaimer</Link>
          {/* Both sign-in providers show this page's URL on their consent screen, so it has to be
              reachable from anywhere on the site and not only from the terms it is cited in. */}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="footer-privacy"
                className="hover:text-white transition-colors">Privacy</Link>
          <a href={`mailto:${cfg.support_email}`} data-testid="footer-email" className="hover:text-white transition-colors break-all">{cfg.support_email}</a>
          <a href={`https://wa.me/${cfg.whatsapp}`} target="_blank" rel="noopener noreferrer"
             data-testid="footer-whatsapp" className="hover:text-white transition-colors">WhatsApp</a>
        </div>
      </div>
    </footer>
  );
}
